/**
 * @x9/task-pipeline — canonical task-status state machine.
 *
 * Single source of truth for task statuses, the legal transition table, the
 * next-primary-action resolver, and the history-event shape. Consumed by the
 * native app (apps/mobile), the unified web execute routes (apps/web-x9), and Cloud
 * Functions (functions/). Authored as dependency-free CommonJS so it loads in
 * Node (functions/Jest), Metro (React Native), and Vite without transpilation.
 *
 * Persisted statuses:  pending, accepted, inProgress, completed, denied
 * Display-only status: reopened (status stays `pending`, history has a reopen)
 * History event types: accepted, started, completed, denied, reopened,
 *                      executor_released, reassigned
 */

'use strict';

/** All persisted task statuses, in funnel order. */
const STATUSES = ['pending', 'accepted', 'inProgress', 'completed', 'denied'];

/** Timeline event types (`statusHistory[].type`). */
const EVENT_TYPES = [
  'accepted',
  'started',
  'completed',
  'denied',
  'reopened',
  'executor_released',
  'reassigned',
];

/**
 * Statuses whose acceptance/progress is invalidated when a task is handed to a
 * different assignee: the new assignee must accept (and then start) themselves.
 */
const REASSIGNMENT_RESET_STATUSES = ['accepted', 'inProgress', 'denied'];

/**
 * Base funnel transitions. This is the strict assignee/web matrix and is kept
 * byte-for-byte equal to the legacy `functions` table (a parity test enforces
 * it). Reopen and resume edges are NOT in this map — they require capability
 * context and are resolved by `canTransition`.
 */
const VALID_TRANSITIONS = {
  pending: ['accepted', 'denied'],
  accepted: ['inProgress', 'denied'],
  inProgress: ['completed', 'denied'],
  completed: [],
  denied: [],
};
/**
 * Decide whether a status change is legal.
 *
 * @param {string} from current persisted status
 * @param {string} to   requested status
 * @param {object} [context]
 * @param {boolean} [context.assigneeResuming] assignee resuming a reopened/
 *        previously-engaged task (allows `pending -> inProgress`, skipping
 *        re-accept).
 * @param {boolean} [context.selfAssigned] task is assigned to the actor
 *        themselves — accept is auto-satisfied, so they may start directly
 *        (`pending -> inProgress`).
 * @param {boolean} [context.isOwner] caller owns the task (may reopen/reset a
 *        non-pending task back to `pending`).
 * @param {boolean} [context.executorReleasing] the assignee is RELEASING the
 *        task back to the owner (`executor_released` timeline event): their
 *        acceptance/progress is invalidated so the task returns to `pending`,
 *        mirroring the reassignment reset. Completed work is never rolled back.
 * @returns {boolean}
 */
function canTransition(from, to, context) {
  const ctx = context || {};
  if (from === to) return true; // no-op writes are always allowed
  const base = VALID_TRANSITIONS[from] || [];
  if (base.includes(to)) return true;
  if (from === 'pending' && to === 'inProgress'
      && (ctx.assigneeResuming || ctx.selfAssigned)) {
    return true;
  }
  // Owner reopen/reset: the task owner may always send a task back to the
  // start of the funnel. Terminal reopen (completed/denied -> pending) is the
  // documented case; an owner resetting an in-flight task is also permitted.
  if (ctx.isOwner && to === 'pending' && from !== 'pending') {
    return true;
  }
  // Executor release: the assignee hands the job back, resetting the same
  // statuses a reassignment resets (accepted/inProgress/denied -> pending).
  if (ctx.executorReleasing && to === 'pending'
      && REASSIGNMENT_RESET_STATUSES.includes(from)) {
    return true;
  }
  return false;
}

/**
 * Primary CTA per status, mirroring the web PWA funnel
 * (`pending -> Accept -> accepted -> Start -> inProgress -> Complete`).
 */
const PRIMARY_ACTION = {
  pending: { actionKey: 'accept', nextStatus: 'accepted' },
  accepted: { actionKey: 'start', nextStatus: 'inProgress' },
  inProgress: { actionKey: 'complete', nextStatus: 'completed' },
};

/**
 * Resolve the next primary action for a task.
 *
 * @param {object} task task-like object with a `status` field
 * @param {object} [context]
 * @param {boolean} [context.isReopenedAssignee] assignee returning to a
 *        reopened task — skip re-accept, go straight to "start".
 * @param {boolean} [context.selfAssigned] task is assigned to the actor
 *        themselves — accept is auto-satisfied, go straight to "start".
 * @returns {{actionKey: string, nextStatus: string}|null}
 */
function nextPrimaryAction(task, context) {
  const ctx = context || {};
  const status = (task && task.status) || 'pending';
  if (status === 'pending' && (ctx.isReopenedAssignee || ctx.selfAssigned)) {
    return PRIMARY_ACTION.accepted;
  }
  return PRIMARY_ACTION[status] || null;
}

/**
 * Map a target status to the `statusHistory` event `type` it should emit.
 * `inProgress` is recorded as a `started` event; every other status maps to an
 * event of the same name. Matches the web callable's history writer.
 */
function historyEventTypeForStatus(status) {
  return status === 'inProgress' ? 'started' : status;
}

/**
 * Decide the status a task should hold after being reassigned to a DIFFERENT
 * assignee. Reassigning invalidates the previous assignee's acceptance/progress,
 * so an `accepted`/`inProgress`/`denied` task drops back to `pending` (the new
 * assignee must accept, then start). `pending` and `completed` are unchanged:
 * `pending` has nothing to reset, and finished work is not reopened.
 *
 * @param {string} currentStatus
 * @returns {string|null} the status to reset to, or null when no change applies
 */
function reassignmentStatusReset(currentStatus) {
  return REASSIGNMENT_RESET_STATUSES.includes(currentStatus) ? 'pending' : null;
}

function parseStatusHistory(task) {
  if (!task || !task.statusHistory) return [];
  if (Array.isArray(task.statusHistory)) return task.statusHistory;
  if (typeof task.statusHistory === 'string') {
    try {
      const parsed = JSON.parse(task.statusHistory);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

/** True when a pending task carries an owner `reopened` event in its history. */
function hasReopenEvent(task) {
  return parseStatusHistory(task).some(
    (entry) => entry && entry.type === 'reopened',
  );
}

/**
 * Derive the display-only status. `reopened` is surfaced when a task sits at
 * `pending` with a reopen event in history and the viewer is the assignee;
 * otherwise the raw persisted status is returned.
 *
 * @param {object} task
 * @param {object} [context]
 * @param {boolean} [context.isAssignee] viewer is the assignee/executor.
 * @returns {string}
 */
function deriveDisplayStatus(task, context) {
  const ctx = context || {};
  const status = (task && task.status) || 'pending';
  if (status === 'pending' && ctx.isAssignee && hasReopenEvent(task)) {
    return 'reopened';
  }
  return status;
}

// ---------------------------------------------------------------------------
// Archive policy (prd-task-run-archiving-2026-08)
// ---------------------------------------------------------------------------

/**
 * Archive/retention policy constants — the single tuning point for both the
 * daily auto-archive scheduler (Cloud Functions) and client-side UI copy.
 * Locked decisions D2/D3 (2026-08-05):
 *  - Only `completed` tasks are archivable (denied tasks stay active until
 *    reassigned via owner reopen, or deleted).
 *  - Settled items auto-archive 30 days after their terminal history event.
 *  - Volume caps: the newest 50 settled tasks / 25 settled runs stay active
 *    regardless of age; overflow archives early.
 */
const ARCHIVE_POLICY = Object.freeze({
  /** Days after settlement before auto-archive. */
  archiveAfterDays: 30,
  /** Max settled (completed) tasks kept active per user. */
  maxActiveSettledTasks: 50,
  /** Max settled (completed/resolved) runs kept active per owner. */
  maxActiveSettledRuns: 25,
});

/** Task statuses eligible for archiving (auto and manual). */
const ARCHIVABLE_TASK_STATUSES = ['completed'];

/**
 * True when a task may be archived: terminal `completed` status, not a
 * delete-tombstone, and not already archived. Ownership is enforced by
 * Firestore rules / callers, not here.
 *
 * @param {object} task task-like object
 * @returns {boolean}
 */
function isTaskArchivable(task) {
  if (!task || task.deleted === true || task.archived === true) return false;
  return ARCHIVABLE_TASK_STATUSES.includes(task.status);
}

/**
 * Resolve the settlement timestamp of a completed task: the newest terminal
 * (`completed`) event in `statusHistory`, falling back to `updatedAt`. Returns
 * epoch milliseconds, or null when no timestamp can be derived (callers should
 * treat null as "not eligible for age-based archiving").
 *
 * @param {object} task task-like object
 * @returns {number|null}
 */
function taskSettledAtMs(task) {
  if (!task) return null;
  const history = parseStatusHistory(task);
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry && entry.type === 'completed' && entry.timestamp) {
      const ms = Date.parse(entry.timestamp);
      if (!Number.isNaN(ms)) return ms;
    }
  }
  if (task.updatedAt) {
    const ms = Date.parse(task.updatedAt);
    if (!Number.isNaN(ms)) return ms;
  }
  return null;
}

module.exports = {
  STATUSES,
  EVENT_TYPES,
  REASSIGNMENT_RESET_STATUSES,
  VALID_TRANSITIONS,
  PRIMARY_ACTION,
  canTransition,
  nextPrimaryAction,
  historyEventTypeForStatus,
  reassignmentStatusReset,
  parseStatusHistory,
  hasReopenEvent,
  deriveDisplayStatus,
  ARCHIVE_POLICY,
  ARCHIVABLE_TASK_STATUSES,
  isTaskArchivable,
  taskSettledAtMs,
};
